import { useEffect, useRef, useState, useCallback } from 'react';

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

interface UseWebSocketOptions {
  stackId: string;
  customerId: string;
  onMessage: (data: Record<string, unknown>) => void;
}

export function useWebSocket({ stackId, customerId, onMessage }: UseWebSocketOptions) {
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  // Use ref for onMessage to avoid reconnecting when callback changes
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    // Determine WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const apiHost = import.meta.env.PROD ? 'api.fanks.ai' : 'localhost:8787';
    const wsUrl = `${wsProtocol}//${apiHost}/v1/widget/connect/${stackId}/${customerId}`;

    console.log('[Fanks] Connecting to WebSocket:', wsUrl);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[Fanks] WebSocket connected');
        setConnectionState('connected');
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Use ref to always call latest callback
          onMessageRef.current(data);
        } catch (error) {
          console.error('[Fanks] Failed to parse message:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('[Fanks] WebSocket closed:', event.code, event.reason);
        setConnectionState('disconnected');
        scheduleReconnect();
      };

      ws.onerror = (error) => {
        console.error('[Fanks] WebSocket error:', error);
        setConnectionState('error');
      };
    } catch (error) {
      console.error('[Fanks] WebSocket connection error:', error);
      setConnectionState('error');
      scheduleReconnect();
    }
  }, [stackId, customerId]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
    reconnectAttemptsRef.current++;

    reconnectTimeoutRef.current = window.setTimeout(() => {
      setConnectionState('connecting');
      connect();
    }, delay);
  }, [connect]);

  const send = useCallback((data: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    } else {
      console.warn('[Fanks] WebSocket not connected, message not sent');
    }
  }, []);

  // Initial connection
  useEffect(() => {
    connect();

    // Ping to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        send({ type: 'ping' });
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      wsRef.current?.close();
    };
  }, [connect, send]);

  return { connectionState, send };
}
